import { useContext } from 'react';

import { Link } from '@mui/material';

import {
  EllipsisWithTooltip,
  Icons,
  Tooltip,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { Link as RouterLink } from 'components/router';
import { WoopIndicator } from 'features/cluster/workload-optimization/_components/ui/WoopIndicator';
import { useIsCustomMetricsEnabled } from 'features/cluster/workload-optimization/_hooks/permissions/useIsCustomMetricsEnabled';
import { useShowHpaV2 } from 'features/cluster/workload-optimization/_hooks/permissions/useShowHpaV2';
import { OptimizationWorkloadsListContext } from 'features/cluster/workload-optimization/workloads-list/context';
import {
  isOptimizationRightsizingOverridden,
  PreparedWorkload,
} from 'features/workload-optimization';

export const WorkloadNameCell = ({
  workload,
  to,
}: {
  workload: PreparedWorkload;
  to?: string;
}) => {
  const { id, name, kind, namespace, isVerticalScalingEnabled, isJvmWorkload } =
    workload;

  const linkTo = to ?? id;

  const { isVpaOverridden } = isOptimizationRightsizingOverridden(
    workload.workloadOverrides
  );
  const { showHpaV2 } = useShowHpaV2();
  const isCustomMetricsEnabled = useIsCustomMetricsEnabled();
  const { isNamespaceWritable } = useContext(OptimizationWorkloadsListContext);
  const isCurrentNamespaceWritable = isNamespaceWritable(namespace);

  return (
    <Stack
      direction="row"
      alignItems="center"
      columnGap={8}
      onClick={(event) => event.stopPropagation()}
      sx={{ width: '100%', minWidth: 0 }}
    >
      {!showHpaV2 && (
        <Tooltip
          title={
            <Stack direction="row" alignItems="center" columnGap={4}>
              <WoopIndicator
                variant={isVerticalScalingEnabled ? 'on' : 'off'}
              />
              <Typography variant="P12R">
                {isVerticalScalingEnabled
                  ? 'Optimization ON:'
                  : 'Optimization OFF:'}
              </Typography>
              <Typography variant="P12B">
                {isVpaOverridden ? 'Overrides policy' : 'Follows policy'}
              </Typography>
            </Stack>
          }
          placement="left"
          arrow
        >
          <div>
            <WoopIndicator variant={isVerticalScalingEnabled ? 'on' : 'off'} />
          </div>
        </Tooltip>
      )}
      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" columnGap={4}>
          <Link component={RouterLink} to={linkTo} underline="none" noWrap>
            <EllipsisWithTooltip
              TypographyProps={{
                variant: 'P12M',
                width: '100%',
              }}
            >
              {name}
            </EllipsisWithTooltip>
          </Link>
          {!isCurrentNamespaceWritable && (
            <Icons.Lock
              size={16}
              css={({ palette }) => ({
                flexShrink: 0,
                color: palette.blue[500],
              })}
            />
          )}
        </Stack>

        {isCustomMetricsEnabled && isJvmWorkload ? (
          <Stack direction="row" flexWrap="nowrap">
            <Typography variant="P10B" noWrap data-testid="jvm-marker">
              JVM&nbsp;|&nbsp;
            </Typography>
            <Typography
              variant="P10R"
              color={tokens.theme.fg.structural.secondary}
              noWrap
            >
              {kind}
            </Typography>
          </Stack>
        ) : (
          <Typography
            variant="P10R"
            color={tokens.theme.fg.structural.secondary}
            noWrap
          >
            {kind}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
